import React from 'react';

import { DisclosurePanel } from './components/AppShellPrimitives';

function cleanText(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function uniqueItems(items, limit = 4) {
  const seen = new Set();
  const ordered = [];
  (items || []).forEach((item) => {
    const cleaned = cleanText(item);
    if (!cleaned || seen.has(cleaned)) {
      return;
    }
    seen.add(cleaned);
    ordered.push(cleaned);
  });
  return ordered.slice(0, limit);
}

function evidencePostureNeeds(posture) {
  if (!posture) {
    return [];
  }
  return uniqueItems([posture.low_data_reason, posture.partial_data_reason, posture.stale_reason]);
}

function validationResultCount(targetedValidation) {
  if (targetedValidation?.latest_result_summary) {
    return Math.max(1, targetedValidation?.result_run_count || 0);
  }
  return targetedValidation?.result_run_count || 0;
}

function referenceNextEvidenceForValidationState(reference, targetedValidation) {
  const nextEvidence = reference?.next_evidence_needed || [];
  if (validationResultCount(targetedValidation) === 0) {
    return nextEvidence;
  }
  return nextEvidence.filter((item) => !/run .*validation pack|attach .*result evidence/i.test(cleanText(item)));
}

export function EvidenceNeededGuidanceCard({
  title = 'Evidence needed',
  canSay,
  cannotSay,
  nextSteps,
  className = '',
  framed = true,
  compact = false,
}) {
  const canSayItems = uniqueItems(canSay, 3);
  const cannotSayItems = uniqueItems(cannotSay, 3);
  const nextStepItems = uniqueItems(nextSteps, 5);

  if (!canSayItems.length && !cannotSayItems.length && !nextStepItems.length) {
    return null;
  }

  const classes = [framed ? 'detail-card' : '', 'evidence-needed-guidance-card', className].filter(Boolean).join(' ');
  const primaryNextStep = nextStepItems[0] || cannotSayItems[0] || canSayItems[0];

  if (compact) {
    return (
      <article className={classes}>
        <div className="workflow-mode-header">
          <strong>{title}</strong>
          <span className="chip targeted-validation-meta-chip">Trace review</span>
        </div>
        <p className="evidence-needed-primary">
          <span>Next evidence</span>
          {primaryNextStep}
        </p>
        <DisclosurePanel title="Why this evidence" className="evidence-needed-disclosure" summaryMeta="Details">
          <GuidanceSections canSayItems={canSayItems} cannotSayItems={cannotSayItems} nextStepItems={nextStepItems} />
        </DisclosurePanel>
      </article>
    );
  }

  return (
    <article className={classes}>
      <div className="workflow-mode-header">
        <strong>{title}</strong>
        <span className="chip targeted-validation-meta-chip">Trace review</span>
      </div>
      <GuidanceSections canSayItems={canSayItems} cannotSayItems={cannotSayItems} nextStepItems={nextStepItems} />
    </article>
  );
}

function GuidanceSections({ canSayItems, cannotSayItems, nextStepItems }) {
  return (
    <div className="stack compact-stack eval-pack-guidance">
      {canSayItems.length ? (
        <div className="eval-pack-guidance-block">
          <strong>What we can say now</strong>
          <ul className="eval-pack-guidance-list">
            {canSayItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {cannotSayItems.length ? (
        <div className="eval-pack-guidance-block">
          <strong>What we cannot say yet</strong>
          <ul className="eval-pack-guidance-list">
            {cannotSayItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {nextStepItems.length ? (
        <div className="eval-pack-guidance-block">
          <strong>Next evidence</strong>
          <ul className="eval-pack-guidance-list">
            {nextStepItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function buildFindingEvidenceGuidance(findingDetail, findingComparison) {
  const finding = findingDetail?.finding || {};
  const targetedValidation = findingDetail?.targeted_validation || {};
  const reference = finding.reference;
  const referenceNextEvidence = referenceNextEvidenceForValidationState(reference, targetedValidation);
  const comparisonAvailable = Boolean(findingComparison?.comparison_available);
  const hasStructuredEvidence = Boolean((findingDetail?.observations || []).length || (findingDetail?.signals || []).length);
  const hasWorkflow = Boolean(findingDetail?.workflow?.id || finding.workflow_id);
  const canSay = [
    hasStructuredEvidence
      ? 'The current finding is grounded in persisted observations or signals from the latest analysis window.'
      : 'The current finding summary can be reviewed, but structured supporting evidence is still sparse.',
    reference ? reference.partner_explanation : finding.customer_language_summary || finding.summary,
    comparisonAvailable ? findingComparison.summary : null,
  ];
  const cannotSay = [
    !comparisonAvailable
      ? 'It cannot prove the behavior improved until comparable follow-up runtime evidence exists for the same workflow and analysis version.'
      : null,
    !targetedValidation.active_pack_id
      ? 'It cannot make a validation-backed claim until targeted validation exists where this finding supports it.'
      : null,
    targetedValidation.active_pack_id && !targetedValidation.result_run_count
      ? 'It cannot use the validation pack as proof until result evidence is attached.'
      : null,
    !hasWorkflow ? 'It cannot preserve a workflow-level before-and-after story without stable workflow lineage.' : null,
    ...(reference?.overclaim_limits || []).slice(0, 1),
  ];
  const nextSteps = [
    !hasWorkflow ? 'Bind follow-on evidence to a stable workflow before using this finding in a selective review.' : null,
    targetedValidation.eligibility_status === 'eligible' && !targetedValidation.active_pack_id
      ? 'Create the targeted validation pack for this finding.'
      : null,
    targetedValidation.active_pack_id && !targetedValidation.result_run_count
      ? 'Attach validation result evidence to the active pack.'
      : null,
    !comparisonAvailable && findingComparison?.reason?.includes('different analysis versions')
      ? 'Re-run or compare analyses produced by the same analysis engine version.'
      : null,
    !comparisonAvailable && hasWorkflow ? 'Upload post-fix runtime evidence for this same workflow.' : null,
    ...referenceNextEvidence,
    ...evidencePostureNeeds(findingDetail?.evidence_posture),
  ];
  return { canSay, cannotSay, nextSteps };
}

export function buildWorkflowEvidenceGuidance(workflowDetail) {
  const runCount = workflowDetail?.analysis_window?.run_count || 0;
  const trend = workflowDetail?.trend || {};
  const coverage = workflowDetail?.validation_coverage || {};
  const proof = workflowDetail?.validation_proof || {};
  const candidateCount = (workflowDetail?.targeted_validation_candidates || []).length;
  const canSay = [
    runCount > 0
      ? `The latest workflow analysis covers ${runCount} ${runCount === 1 ? 'run' : 'runs'} in the current window.`
      : 'The workflow exists, but no completed analysis window is ready yet.',
    workflowDetail?.reliability_verdict?.summary,
    coverage.summary,
  ];
  const cannotSay = [
    trend.low_data_reason ? 'It cannot make a strong runtime movement claim while the workflow window is sparse or not comparable.' : null,
    coverage.status !== 'covered' ? 'It cannot say all current risky behaviors are covered by targeted validation.' : null,
    proof.status !== 'comparable' ? 'It cannot claim validation improved without comparable current and prior result runs.' : null,
  ];
  const nextSteps = [
    trend.low_data_reason ? 'Collect more runs in this same workflow and rerun analysis.' : null,
    candidateCount > 0 && coverage.status !== 'covered' ? 'Create validation packs for eligible workflow findings.' : null,
    coverage.low_data_reasons?.length ? coverage.low_data_reasons.join(' ') : null,
    proof.low_data_reason,
    ...(workflowDetail?.representative_traces?.length ? [] : ['Upload representative traces with enough supported fields for review.']),
    ...evidencePostureNeeds(workflowDetail?.evidence_posture),
  ];
  return { canSay, cannotSay, nextSteps };
}

export function buildTargetedValidationEvidenceGuidance(targetedValidation, reference) {
  const hasPack = Boolean(targetedValidation?.active_pack_id);
  const resultCount = validationResultCount(targetedValidation);
  const referenceNextEvidence = referenceNextEvidenceForValidationState(reference, targetedValidation);
  const canSay = [
    targetedValidation?.eligibility_reason,
    reference?.validation_posture ? `Reference posture: ${reference.validation_posture.replaceAll('_', ' ')}.` : null,
  ];
  const cannotSay = [
    !hasPack ? 'It cannot use validation as proof until a pack or external validation path exists.' : null,
    hasPack && resultCount === 0 ? 'It cannot claim the risky behavior improved until validation results are attached.' : null,
    targetedValidation?.follow_up_route === 'unavailable'
      ? 'It cannot prepare an in-app pack from the current provenance alone.'
      : null,
  ];
  const nextSteps = [
    targetedValidation?.follow_up_route === 'in_app_pack' && !hasPack ? 'Create the targeted validation pack.' : null,
    hasPack && resultCount === 0 ? 'Attach validation result evidence to the active pack.' : null,
    targetedValidation?.follow_up_route === 'external_validation' ? 'Run the external validation path and attach result evidence when available.' : null,
    targetedValidation?.follow_up_route === 'unavailable' ? 'Collect traces with the supported fields needed for this finding family.' : null,
    ...referenceNextEvidence,
  ];
  return { canSay, cannotSay, nextSteps };
}

export function buildRuntimeUploadEvidenceGuidance({ workflowOptions, selectedWorkflow, selectedFormat }) {
  const hasWorkflows = Boolean(workflowOptions?.length);
  const canSay = [
    hasWorkflows && selectedWorkflow
      ? `This upload will append ${selectedFormat?.label || 'runtime'} evidence to ${selectedWorkflow.name}.`
      : 'Runtime evidence can strengthen claims once it is bound to a workflow.',
  ];
  const cannotSay = [
    'This follow-on upload path does not create historical baselines or prove a fix by itself.',
    !hasWorkflows ? 'It cannot preserve workflow lineage until a workflow exists for the system.' : null,
  ];
  const nextSteps = [
    hasWorkflows ? 'Upload post-fix or follow-on traces to the same workflow you plan to compare.' : 'Create or analyze a workflow before uploading follow-on evidence.',
    'After processing completes, review Compare for runtime movement and targeted validation proof separately.',
  ];
  return { canSay, cannotSay, nextSteps };
}
